import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { setSession } from "@/lib/auth";
import { postWithRetry } from "@/lib/retryPost";
import { INITIAL_AUTH_FORM } from "@/components/auth/signupConstants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Encapsulates all Sign In / Create Account state + submission logic so
// SignInModal (and its Login/Register form components) stay presentational.
export const useAuthForm = (onClose) => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState(INITIAL_AUTH_FORM);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const toggleArrayValue = (key, value) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(value) ? f[key].filter((v) => v !== value) : [...f[key], value],
    }));

  const isDriverRole = form.role !== "carrier" && form.role !== "broker";
  const isCarrierRole = form.role === "carrier";
  const isBrokerRole = form.role === "broker";

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await postWithRetry(
        `${API}/auth/login`,
        { email: form.email, password: form.password },
        () => toast.message("RelayRigs is waking up — retrying automatically...")
      );
      setSession(data.user);
      toast.success(`Welcome back, ${data.user.name || data.user.email}!`);
      onClose();
      if ((data.user.role || "").toLowerCase() === "broker") {
        window.location.href = "/broker-portal";
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Incorrect email or password.");
    } finally {
      setLoading(false);
    }
  };

  // Sign in to the real RelayRigs app account via Emergent-managed Google Auth.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleSignIn = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Fire this local profile save FIRST (fire-and-forget) so brokerage/driver/carrier
    // details are never lost even if the real upstream account creation below fails
    // (e.g. broker role not supported upstream yet). Service radius/endorsements/trailer
    // types/broker directory fields aren't accepted by the real signup endpoint — stored
    // locally here regardless of upstream outcome.
    axios.post(`${API}/onboarding`, {
      email: form.email,
      role: form.role,
      city: form.city,
      state: form.state,
      service_radius_miles: Number(form.serviceRadius) || null,
      endorsements: isDriverRole ? form.endorsements : [],
      trailer_types: isDriverRole ? form.trailerTypes : [],
      dba: isBrokerRole ? form.dba || null : null,
      business_address: isBrokerRole ? form.businessAddress || null : null,
      website: isBrokerRole ? form.website || null : null,
      operating_areas: isBrokerRole ? form.operatingAreas || null : null,
      freight_types: isBrokerRole ? form.freightTypes : [],
      estimated_monthly_loads: isBrokerRole ? form.estimatedMonthlyLoads || null : null,
      estimated_driver_network: isBrokerRole ? form.estimatedDriverNetwork || null : null,
      ap_contact: isBrokerRole ? form.apContact || null : null,
      company_name: isCarrierRole || isBrokerRole ? form.companyName || null : null,
      mc_number: isCarrierRole || isBrokerRole ? form.mcNumber || null : null,
    }).catch(() => {});

    try {
      const { data } = await postWithRetry(
        `${API}/auth/register`,
        {
          name: form.name, email: form.email, password: form.password, role: form.role,
          phone: form.phone || null,
          home_city: form.city || null,
          home_state: form.state || null,
          cdl_class: isDriverRole ? form.cdlClass : null,
          cdl_number: isDriverRole ? form.cdlNumber || null : null,
          experience_years: isDriverRole ? Number(form.experienceYears) || null : null,
          company_name: isCarrierRole || isBrokerRole ? form.companyName || null : null,
          dot_number: isCarrierRole ? form.dotNumber || null : null,
          mc_number: isCarrierRole || isBrokerRole ? form.mcNumber || null : null,
          fleet_size: isCarrierRole ? Number(form.fleetSize) || null : null,
          accepted_legal: true,
          legal_version: "1.1",
          age_confirmed: form.ageConfirmed,
        },
        () => toast.message("RelayRigs is waking up — retrying automatically...")
      );
      setSession(data.user);

      toast.success("Account created! Redirecting to your RelayRigs subscription...");
      const checkout = await postWithRetry(
        `${API}/billing/checkout`,
        { plan: form.role },
        () => toast.message("RelayRigs is waking up — retrying automatically...")
      );
      onClose();
      window.location.href = checkout.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create account.");
      setLoading(false);
    }
  };

  return {
    form, setForm, update, toggleArrayValue,
    loading, showPassword, setShowPassword,
    isDriverRole, isCarrierRole, isBrokerRole,
    handleLogin, handleGoogleSignIn, handleRegister,
  };
};
